import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, ModalShell } from "@workspace/ui";
import { useAuthStore } from "../../state/auth-store";
import { getDniValidationMessageKey, normalizeIdentityDoc } from "./dni";
import { identityService } from "./identity-service";

type PromptResolver = {
  resolve: (dni: string) => void;
  reject: (error: Error) => void;
};

function resolvePromptErrorMessage(
  t: (key: string) => string,
  errorStatusCode?: number,
  errorMessage?: string,
  errorMessageKey?: string,
) {
  if (errorStatusCode === 409) {
    return t("prompt.errors.duplicateDocument");
  }

  if (errorMessage) {
    return errorMessage;
  }

  return errorMessageKey ? t(errorMessageKey) : undefined;
}

export function IdentityPromptDialog() {
  const { t } = useTranslation("identity");
  const logout = useAuthStore((state) => state.logout);
  const resolverRef = useRef<PromptResolver | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dni, setDni] = useState("");
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    return identityService.registerPromptHandler(async (request) => {
      setDni(request.currentDni ?? "");
      setError(
        resolvePromptErrorMessage(
          t,
          request.errorStatusCode,
          request.errorMessage,
          request.errorMessageKey,
        ),
      );
      setIsSubmitting(false);
      setIsOpen(true);

      return new Promise<string>((resolve, reject) => {
        resolverRef.current = { resolve, reject };
      });
    });
  }, [t]);

  const handleCloseSession = () => {
    resolverRef.current?.reject(new Error("IDENTIFICATION_CANCELLED"));
    resolverRef.current = null;
    setIsOpen(false);
    logout();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedDni = normalizeIdentityDoc(dni);
    const validationMessageKey = getDniValidationMessageKey(normalizedDni);

    if (validationMessageKey) {
      setError(t(validationMessageKey));
      return;
    }

    setIsSubmitting(true);
    setError(undefined);
    resolverRef.current?.resolve(normalizedDni);
    resolverRef.current = null;
    setIsOpen(false);
    setIsSubmitting(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ModalShell
      open
      onClose={handleCloseSession}
      title={t("prompt.title")}
      subtitle={t("prompt.description")}
      shrink
      minWidth="min-w-[20rem]"
      maxWidth="max-w-md"
      showCloseButton={false}
      closeOnEscape={false}
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={handleCloseSession} disabled={isSubmitting}>
            {t("prompt.actions.logout")}
          </Button>
          <Button form="identity-prompt-form" type="submit" disabled={isSubmitting}>
            {t("prompt.actions.save")}
          </Button>
        </div>
      }
    >
        <form id="identity-prompt-form" className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label
              className="text-sm font-medium text-content-secondary"
              htmlFor="identity-dni"
            >
              {t("prompt.form.identityLabel")}
            </label>
            <Input
              id="identity-dni"
              value={dni}
              onChange={(event) => setDni(event.target.value)}
              onBlur={() =>
                setDni((currentDni) => normalizeIdentityDoc(currentDni))
              }
              placeholder={t("prompt.form.identityPlaceholder")}
              autoComplete="off"
              disabled={isSubmitting}
            />
            <p className="text-xs text-content-muted">{t("prompt.form.helper")}</p>
            {error ? <p className="text-sm text-state-danger">{error}</p> : null}
          </div>

        </form>
    </ModalShell>
  );
}
